import * as React from "react";
import { cn } from "@/lib/utils";
import { useScopeEnabled } from "@/hooks/use-scope-enabled";

// Other div props (data attributes…) land on the wrapper div.
interface ScopeWrapperProps extends React.ComponentProps<"div"> {
  children: React.ReactNode;
  enabled?: boolean;
  color?: "destructive" | "warning" | "success" | "foreground" | "primary";
  card?: boolean;
  onClick?: () => void;
  className?: string;
}

export function ScopeWrapper({
  children,
  enabled = true,
  color = "foreground",
  card = false,
  onClick,
  className,
  ...props
}: ScopeWrapperProps) {
  // Shared app-wide subscription (see use-scope-enabled).
  const scopeGlobalEnabled = useScopeEnabled();

  const shouldShowScope = enabled && scopeGlobalEnabled;

  const activeClass = {
    destructive: "group-active/scope:border-destructive",
    warning: "group-active/scope:border-warning",
    success: "group-active/scope:border-success",
    foreground: "group-active/scope:border-foreground",
    primary: "group-active/scope:border-primary",
  }[color];

  const cornerSize = card ? "size-2" : "size-1.5";

  // The corners sit on top (z-50, like Button's): drawn before the children,
  // they were hidden under the wrapped card's ring and the neighbours around it.
  // The wrapper div renders either way: returning a bare fragment when scope
  // was off changed the element type, so flipping the switch remounted the
  // whole subtree (it closed the nav-user menu the switch lives in).
  return (
    <div {...props} className={cn("group/scope relative inline-block", className)} onClick={onClick}>
      {shouldShowScope && (
        <>
          {/* Top left */}
          <div
            className={cn(
              "rounded-tl-[calc(var(--radius-xs)/2)] z-50 border-muted-foreground/60 absolute -top-0.75 -left-0.75 border-l border-t opacity-0 transition-all duration-100 pointer-events-none",
              "group-hover/scope:opacity-100 group-hover/scope:-translate-x-px group-hover/scope:-translate-y-px",
              "group-active/scope:translate-x-0 group-active/scope:translate-y-0",
              cornerSize,
              activeClass
            )}
          />
          {/* Top right */}
          <div
            className={cn(
              "rounded-tr-[calc(var(--radius-xs)/2)] z-50 border-muted-foreground/60 absolute -top-0.75 -right-0.75 border-r border-t opacity-0 transition-all duration-100 pointer-events-none",
              "group-hover/scope:opacity-100 group-hover/scope:translate-x-px group-hover/scope:-translate-y-px",
              "group-active/scope:translate-x-0 group-active/scope:translate-y-0",
              cornerSize,
              activeClass
            )}
          />
          {/* Bottom left */}
          <div
            className={cn(
              "rounded-bl-[calc(var(--radius-xs)/2)] z-50 border-muted-foreground/60 absolute -bottom-0.75 -left-0.75 border-l border-b opacity-0 transition-all duration-100 pointer-events-none",
              "group-hover/scope:opacity-100 group-hover/scope:-translate-x-px group-hover/scope:translate-y-px",
              "group-active/scope:translate-x-0 group-active/scope:translate-y-0",
              cornerSize,
              activeClass
            )}
          />
          {/* Bottom right */}
          <div
            className={cn(
              "rounded-br-[calc(var(--radius-xs)/2)] z-50 border-muted-foreground/60 absolute -bottom-0.75 -right-0.75 border-r border-b opacity-0 transition-all duration-100 pointer-events-none",
              "group-hover/scope:opacity-100 group-hover/scope:translate-x-px group-hover/scope:translate-y-px",
              "group-active/scope:translate-x-0 group-active/scope:translate-y-0",
              cornerSize,
              activeClass
            )}
          />
        </>
      )}
      {children}
    </div>
  );
}